import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flame, Clock, CheckCircle2, Save, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useWarmup } from "@/hooks/useWarmup";
import { toast } from "@/hooks/use-toast";

export default function AlunoAquecimento() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { prompts, responses, isLoading, saveDraft, submit } = useWarmup();
  const [resposta, setResposta] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const prompt = prompts.find(p => p.id === id);
  const existingResponse = responses.find(r => r.prompt_id === id);

  useEffect(() => {
    if (existingResponse?.answer_text) {
      setResposta(existingResponse.answer_text);
    }
    if (existingResponse?.status === "submitted") {
      setSubmitted(true);
    }
  }, [existingResponse]);

  const handleSubmit = async () => {
    if (!resposta.trim() || !id) return;
    setIsSubmitting(true);
    try {
      await submit.mutateAsync({ promptId: id, answerText: resposta });
      setSubmitted(true);
    } catch {
      toast({ title: "Erro ao enviar", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveRascunho = async () => {
    if (!resposta.trim() || !id) return;
    try {
      await saveDraft.mutateAsync({ promptId: id, answerText: resposta });
      toast({ title: "Rascunho salvo!" });
    } catch {
      toast({ title: "Erro ao salvar rascunho", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-success" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center max-w-sm">
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Aquecimento concluído!</h1>
          <p className="text-muted-foreground mb-8">Sua resposta foi registrada e adicionada ao seu histórico.</p>
          <div className="bg-accent/10 rounded-2xl p-4 border border-accent/20 mb-8 text-left">
            <p className="text-sm font-medium text-foreground mb-1">✨ Sinal de Carreira atualizado</p>
            <p className="text-xs text-muted-foreground">Continue completando atividades para evoluir seu status</p>
          </div>
          <div className="space-y-3">
            <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/carreira")}>Voltar para Carreira</Button>
            <Button variant="outline" size="lg" className="w-full" onClick={() => navigate("/aluno/carreira/historico")}>Ver Histórico</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/carreira")}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Aquecimento</span>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-accent/10 rounded-xl p-4 mb-6 border border-accent/20">
        <p className="text-sm font-medium text-foreground">Isso não é teste. É aquecimento.</p>
        <p className="text-xs text-muted-foreground mt-1">Reflita com calma. Não existe resposta certa ou errada.</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
            <Flame className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />10-15 min
          </div>
        </div>
        <h1 className="font-heading text-lg font-bold text-foreground mb-2">{prompt?.title ?? "Aquecimento"}</h1>
        <p className="text-sm text-muted-foreground">{prompt?.question ?? ""}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
        <label className="text-sm font-medium text-foreground mb-2 block">Sua resposta</label>
        <Textarea value={resposta} onChange={(e) => setResposta(e.target.value)} placeholder="Escreva sua reflexão aqui..." className="min-h-[200px] resize-none" />
        <p className="text-xs text-muted-foreground mt-2 text-right">{resposta.length} caracteres</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="space-y-3">
        <Button variant="hero" size="xl" className="w-full" onClick={handleSubmit} disabled={!resposta.trim() || isSubmitting}>
          {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Enviando...</>) : "Enviar resposta"}
        </Button>
        <Button variant="outline" size="lg" className="w-full" onClick={handleSaveRascunho} disabled={!resposta.trim()}>
          <Save className="w-4 h-4" />Salvar rascunho
        </Button>
      </motion.div>
    </div>
  );
}
